import { useMemo } from 'react';
import useSWR, { KeyedMutator } from 'swr';
import {
    customSWRConfig,
    customSWRResponse,
    FieldDefinitionMap,
    IFieldDefinition,
    PaginatedData
} from '../model/types';
import { localizedSearchTermForApi } from '../utils/helpers';
import { refactorFieldOptions } from './ticketDetailPage/useFieldDefinitionListFromAgentForm';
import useSWRCacheKeys from './useSWRCacheKeys';

/**
 * this hook returns fieldDefinition list and listMap that have diffrent type for diffrent usage.
 * when using this hook, rely on the following information.
 *
 * @list paginated fieldDefinition list, prefered usage; create fields table and edit custom fields, @return PaginatedData<IFieldDefinition>
 * @listMap fieldDefination list object with manipulated options for select and multi select field type (option values are converted to option ids), prefered usage; inputs, @return FieldDefinitionMap
 */
function usePaginatedUserFieldDefinitionList<
    T extends { list: PaginatedData<IFieldDefinition>; listMap: FieldDefinitionMap }
>(
    params?: { size?: number; page?: number; isActive?: boolean; sort?: string; searchTerm?: string },
    config: customSWRConfig = {}
): customSWRResponse<T> {
    const keys = useSWRCacheKeys(
        `/user-fields${
            params
                ? `?${params?.isActive ? 'active' : 'inactive'}${params?.size ? `&size=${params?.size}` : ''}${
                      params?.page ? `&page=${params?.page}` : ''
                  }${params?.sort ? `&sort=${params?.sort}` : ''}${
                      params?.searchTerm ? `&searchTerm=${localizedSearchTermForApi(params?.searchTerm)}` : ''
                  }`
                : ''
        }`
    );

    const { data, error, isLoading, mutate } = useSWR<PaginatedData<IFieldDefinition>>(keys, { ...config });

    const fields = useMemo(
        () =>
            data
                ? {
                      list: {
                          ...data,
                          content: data?.content?.map((fd) => ({ ...refactorFieldOptions(fd) }))
                      },
                      listMap: data?.content?.reduce(
                          (acc, fd) => ({ ...acc, [fd.key]: refactorFieldOptions(fd) }),
                          {} as FieldDefinitionMap
                      )
                  }
                : ({ list: data?.content, listMap: data?.content } as unknown as T),
        [data]
    );

    return {
        data: fields as T,
        error,
        isLoading,
        mutate: mutate as unknown as KeyedMutator<T>
    };
}
function usePaginatedUserFieldDefinitionListNoQuaryParams<
    T extends { list: PaginatedData<IFieldDefinition>; listMap: FieldDefinitionMap }
>(config: customSWRConfig = {}): customSWRResponse<T> {
    const keys = useSWRCacheKeys(`/user-fields`);

    const { data, error, isLoading, mutate } = useSWR<PaginatedData<IFieldDefinition>>(keys, { ...config });

    const fields = useMemo(
        () =>
            data
                ? {
                      list: {
                          ...data,
                          content: data?.content?.map((fd) => ({ ...refactorFieldOptions(fd) }))
                      },
                      listMap: data?.content?.reduce(
                          (acc, fd) => ({ ...acc, [fd.key]: refactorFieldOptions(fd) }),
                          {} as FieldDefinitionMap
                      )
                  }
                : ({ list: data?.content, listMap: data?.content } as unknown as T),
        [data]
    );

    return {
        data: fields as T,
        error,
        isLoading,
        mutate: mutate as unknown as KeyedMutator<T>
    };
}

function usePaginatedUserFieldDefinitionListOnlyStatusParam<
    T extends { list: PaginatedData<IFieldDefinition>; listMap: FieldDefinitionMap }
>(isActive: boolean, config: customSWRConfig = {}): customSWRResponse<T> {
    const keys = useSWRCacheKeys(`/user-fields?${isActive ? 'active' : 'inactive'}`);

    const { data, error, isLoading, mutate } = useSWR<PaginatedData<IFieldDefinition>>(keys, { ...config });

    const fields = useMemo(
        () =>
            data
                ? {
                      list: {
                          ...data,
                          content: data?.content?.map((fd) => ({ ...refactorFieldOptions(fd) }))
                      },
                      listMap: data?.content?.reduce(
                          (acc, fd) => ({ ...acc, [fd.key]: refactorFieldOptions(fd) }),
                          {} as FieldDefinitionMap
                      )
                  }
                : ({ list: data?.content, listMap: data?.content } as unknown as T),
        [data]
    );

    return {
        data: fields as T,
        error,
        isLoading,
        mutate: mutate as unknown as KeyedMutator<T>
    };
}

export {
    usePaginatedUserFieldDefinitionList,
    usePaginatedUserFieldDefinitionListNoQuaryParams,
    usePaginatedUserFieldDefinitionListOnlyStatusParam
};
